import {getHighlightRanges} from "@pg-design/search-combobox-module";
import {PRICES_CITIES} from "@pg-rp/navigation";

import {getPricesCityRegionId} from "../../constants/prices_cities_regions";
import {
    PRICES_SEARCH_COPY,
    PRICES_SEARCH_GROUP_LIMIT,
    PRICES_SEARCH_MIN_QUERY_LENGTH,
    PRICES_SEARCH_NUMBERS_PREVIEW,
    PRICES_SEARCH_POPULAR_CITY_SLUGS,
    PRICES_SEARCH_RECENT_SHOWN
} from "../../constants/prices_search";
import type {
    IBuildPricesSearchModelInput,
    IParsedPricesSearchQuery,
    IPricesAddressSearchResult,
    IPricesRecentSearch,
    IPricesRegionSuggestion,
    IPricesRegionSuggestionsResult,
    IPricesSearchAddressItem,
    IPricesSearchBanner,
    IPricesSearchChipItem,
    IPricesSearchCityItem,
    IPricesSearchClearRecentItem,
    IPricesSearchContext,
    IPricesSearchDistrictItem,
    IPricesSearchModel,
    IPricesSearchNearbyItem,
    IPricesSearchNumberItem,
    IPricesSearchNumberList,
    IPricesSearchRetryItem,
    IPricesSearchSection,
    IPricesSearchShowMoreItem,
    IPricesSearchStreetGroup,
    IPricesSearchStreetItem,
    PricesSearchItemKind,
    PricesSearchNavigableItem,
    PricesSearchNumberPrices,
    PricesSearchSectionItem,
    PricesSearchSectionKind
} from "../../types/IPricesSearchModel";
import type {IRcnAddressKey, IRcnAddressSuggestion} from "../../types/IRcnAddress";
import {getAddressLink} from "../get_address_link";
import {getCityLink} from "../get_city_link";
import {formatRcnAddressLabel, formatRcnCityName, formatRcnHouseNumber, formatRcnStreetName} from "./format_rcn_display_name";
import {getDistrictLink} from "./get_district_link";
import {getVoivodeshipByCitySlug} from "./get_voivodeship_by_city_slug";
import {getAutoExpandedStreetKey, getRcnAddressKey, groupRcnAddresses} from "./group_rcn_addresses";
import {findPricesCityByName, getDiacriticsBannerPhrase} from "./resolve_query_diacritics";
import {PRICES_SEARCH_MATCH_SCORE, scoreBestToken} from "./score_match";

type PricesCity = (typeof PRICES_CITIES)[number];
type ResultSectionKind = Extract<PricesSearchSectionKind, "cities" | "districts" | "streets" | "addresses">;
export type PricesCityDistrict = IPricesRegionSuggestion & {citySlug: string; cityName: string};

const COPY = PRICES_SEARCH_COPY;
const EMPTY_RESULT: IPricesAddressSearchResult = {rows: [], capped: false};
const PRICES_CITY_BY_SLUG: ReadonlyMap<string, PricesCity> = new Map(PRICES_CITIES.map((city) => [city.slug, city]));

const toSubtitle = (type: string, detail: string | null): string => (detail ? `${type}${COPY.subtitleSeparator}${detail}` : type);

const toHighlightRanges = (title: string, query: string) => (query ? getHighlightRanges(title, query) : []);

// ---------------------------------------------------------------------------------------------------------------
// Rows
// ---------------------------------------------------------------------------------------------------------------

const buildCityItem = (
    section: PricesSearchSectionKind,
    city: PricesCity,
    highlightQuery: string,
    input: IBuildPricesSearchModelInput
): IPricesSearchCityItem => ({
    id: `${section}:city:${city.slug}`,
    kind: "city",
    isNavigation: true,
    title: city.name,
    highlightRanges: toHighlightRanges(city.name, highlightQuery),
    subtitle: toSubtitle(COPY.subtitles.city, getVoivodeshipByCitySlug(city.slug)),
    metrics: input.metrics.byCitySlug[city.slug],
    href: getCityLink(city.slug),
    searchLabel: city.name,
    citySlug: city.slug,
    regionId: getPricesCityRegionId(city.slug)
});

const buildNumberItem = (row: IRcnAddressSuggestion, citySlug: string): IPricesSearchNumberItem => ({
    id: `streets:number:${getRcnAddressKey(row)}`,
    kind: "number",
    isNavigation: true,
    title: formatRcnHouseNumber(row.number),
    // Building numbers are never bold.
    highlightRanges: [],
    metrics: {transactionsCount: row.transactions_count},
    href: getAddressLink(row),
    searchLabel: formatRcnAddressLabel(row),
    citySlug,
    rcn: {city: row.city, street: row.street, number: row.number}
});

const buildNumberList = (street: IPricesSearchStreetGroup, cityName: string, input: IBuildPricesSearchModelInput): IPricesSearchNumberList => {
    const isShowingAll = input.ui.expandedNumberStreetKeys.includes(street.key);
    const isLoading = input.ui.loadingShowMoreKeys.includes(street.key);
    const allItems = street.rows.map((row) => buildNumberItem(row, street.citySlug));
    const items = isShowingAll ? allItems : allItems.slice(0, PRICES_SEARCH_NUMBERS_PREVIEW);
    const hiddenCount = allItems.length - items.length;
    // An incomplete street may have more numbers than we hold - the link then loads them, without a count.
    const hasMore = !isShowingAll && (hiddenCount > 0 || !street.isComplete);

    return {
        items,
        totalCount: allItems.length,
        isTotalExact: street.isComplete,
        isLoading,
        label: `${COPY.numbersGroupLabel}: ${street.title}, ${cityName}`,
        ...(hasMore
            ? {
                  showMore: {
                      id: `streets:showMore:${street.key}`,
                      kind: "showMore",
                      isNavigation: false,
                      target: "numbers",
                      label: street.isComplete ? `${COPY.showAllNumbers} (${allItems.length})` : COPY.showAllNumbers,
                      hiddenCount: street.isComplete ? hiddenCount : null,
                      streetKey: street.key,
                      isLoading
                  }
              }
            : {})
    };
};

const buildStreetItem = (
    street: IPricesSearchStreetGroup,
    expandedKey: string | null,
    highlightQuery: string,
    input: IBuildPricesSearchModelInput
): IPricesSearchStreetItem => {
    const cityName = formatRcnCityName(street.rawCity);
    const isExpanded = street.key === expandedKey;

    return {
        id: `streets:street:${street.key}`,
        kind: "street",
        // Selecting a street toggles its numbers - there is no street page.
        isNavigation: false,
        title: street.title,
        highlightRanges: toHighlightRanges(street.title, highlightQuery),
        subtitle: toSubtitle(COPY.subtitles.street, cityName),
        // No price per street in RCN; the sum is a lower bound while the street is incomplete.
        metrics: {transactionsCount: street.transactionsSum, transactionsIsLowerBound: !street.isComplete},
        searchLabel: `${street.title}, ${cityName}`,
        citySlug: street.citySlug,
        rcn: {city: street.rawCity, street: street.rawStreet},
        streetKey: street.key,
        isExpanded,
        ...(isExpanded ? {numbers: buildNumberList(street, cityName, input)} : {})
    };
};

const buildAddressItem = (row: IRcnAddressSuggestion, highlightQuery: string): IPricesSearchAddressItem => {
    const city = findPricesCityByName(row.city);
    const cityName = city?.name ?? formatRcnCityName(row.city);
    const title = `${formatRcnStreetName(row.street)} ${formatRcnHouseNumber(row.number)}`;

    return {
        id: `addresses:address:${getRcnAddressKey(row)}`,
        kind: "address",
        isNavigation: true,
        title,
        highlightRanges: toHighlightRanges(title, highlightQuery),
        subtitle: toSubtitle(COPY.subtitles.address, cityName),
        metrics: {transactionsCount: row.transactions_count},
        href: getAddressLink(row),
        searchLabel: formatRcnAddressLabel(row),
        // Grouped rows all belong to one of the 14 cities.
        citySlug: city?.slug ?? "",
        rcn: {city: row.city, street: row.street, number: row.number}
    };
};

// ---------------------------------------------------------------------------------------------------------------
// Sections
// ---------------------------------------------------------------------------------------------------------------

interface ISectionOptions {
    /** False when the source response was capped: the section may hold more than we know. */
    isHiddenCountExact: boolean;
}

const buildSection = (
    kind: ResultSectionKind,
    allItems: PricesSearchSectionItem[],
    {isHiddenCountExact}: ISectionOptions,
    input: IBuildPricesSearchModelInput
): IPricesSearchSection | null => {
    if (allItems.length === 0) {
        return null;
    }

    const isExpanded = input.ui.expandedSections.includes(kind);
    const items = isExpanded ? allItems : allItems.slice(0, PRICES_SEARCH_GROUP_LIMIT);
    const hiddenCount = allItems.length - items.length;
    const hasMore = !isExpanded && (hiddenCount > 0 || !isHiddenCountExact);
    const showMore: IPricesSearchShowMoreItem = {
        id: `${kind}:showMore`,
        kind: "showMore",
        isNavigation: false,
        target: kind,
        // "więcej" counts the rows left; with a capped response the number is unknown, so it is left out.
        label: isHiddenCountExact ? `${COPY.showMore[kind]} (${hiddenCount})` : COPY.showMore[kind],
        hiddenCount: isHiddenCountExact ? hiddenCount : null,
        isLoading: input.ui.loadingShowMoreKeys.includes(kind)
    };

    return {
        kind,
        title: COPY.groups[kind],
        items,
        hiddenCount,
        isHiddenCountExact,
        isExpanded,
        ...(hasMore ? {showMore} : {})
    };
};

// A city named in the query only scopes the streets once other words follow: "warszawa puławska" lists the street,
// not Warszawa, so Enter picks the street. Words shorter than the minimum query ("warszawa k") match no city.
const getCityMatchTokens = (parsed: IParsedPricesSearchQuery): string[] => [
    ...parsed.restTokens.filter((token) => token.length >= PRICES_SEARCH_MIN_QUERY_LENGTH),
    ...(parsed.restTokens.length === 0 && parsed.numberToken === undefined ? parsed.cityTokens.map((cityToken) => cityToken.token) : [])
];

const buildCitiesSection = (parsed: IParsedPricesSearchQuery, highlightQuery: string, input: IBuildPricesSearchModelInput): IPricesSearchSection | null => {
    const tokens = getCityMatchTokens(parsed);
    const transactionsCount = (city: PricesCity) => input.metrics.byCitySlug[city.slug]?.transactionsCount ?? 0;
    const cities = PRICES_CITIES.map((city) => ({city, score: scoreBestToken(city.name, tokens)}))
        .filter(({score}) => score >= PRICES_SEARCH_MATCH_SCORE.wordPrefix)
        .sort((a, b) => b.score - a.score || transactionsCount(b.city) - transactionsCount(a.city))
        .map(({city}) => buildCityItem("cities", city, highlightQuery, input));

    return buildSection("cities", cities, {isHiddenCountExact: true}, input);
};

/**
 * Districts the model lists, in order: those with a prices page, the current city's first (API order kept within both
 * groups). The region API matches any word of `full_name`, so "warszawa puławska" brings every Warsaw district - once
 * other words than a city are typed, a district has to match them by name, and belong to a typed city if there is one.
 */
export const getPricesSearchDistricts = (
    parsed: IParsedPricesSearchQuery,
    regions: IPricesRegionSuggestionsResult | null,
    context: IPricesSearchContext
): PricesCityDistrict[] => {
    const queryCitySlugs = new Set(parsed.cityTokens.map((cityToken) => cityToken.citySlug));
    const matchesQuery = (district: PricesCityDistrict): boolean =>
        parsed.restTokens.length === 0 ||
        (scoreBestToken(district.name, parsed.restTokens) >= PRICES_SEARCH_MATCH_SCORE.wordPrefix &&
            (queryCitySlugs.size === 0 || queryCitySlugs.has(district.citySlug)));

    return (
        (regions?.districts ?? [])
            .filter((district): district is PricesCityDistrict => district.hasPricesPage && district.citySlug !== null && district.cityName !== null)
            .filter(matchesQuery)
            // Stable: the API order stays within both groups.
            .sort((a, b) => Number(b.citySlug === context.citySlug) - Number(a.citySlug === context.citySlug))
    );
};

// Districts suggestions hidden for now, may return later.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const buildDistrictsSection = (parsed: IParsedPricesSearchQuery, highlightQuery: string, input: IBuildPricesSearchModelInput): IPricesSearchSection | null => {
    const districts = getPricesSearchDistricts(parsed, input.regions, input.context).map(
        (district): IPricesSearchDistrictItem => ({
            id: `districts:district:${district.regionId}`,
            kind: "district",
            isNavigation: true,
            title: district.name,
            highlightRanges: toHighlightRanges(district.name, highlightQuery),
            subtitle: toSubtitle(COPY.subtitles.district, district.cityName),
            metrics: input.metrics.byRegionId[district.regionId],
            href: getDistrictLink(district.citySlug, district.region),
            searchLabel: `${district.name}, ${district.cityName}`,
            citySlug: district.citySlug,
            regionId: district.regionId,
            region: district.region
        })
    );

    // A truncated first page may hide more districts than the ones counted.
    return buildSection("districts", districts, {isHiddenCountExact: !input.regions?.isTruncated}, input);
};

const buildResultSections = (
    parsed: IParsedPricesSearchQuery,
    base: IPricesAddressSearchResult,
    input: IBuildPricesSearchModelInput
): IPricesSearchSection[] => {
    // Bold what was typed for the title, the building number excluded (numbers are never bold).
    const highlightQuery = [...parsed.cityTokens.map((cityToken) => cityToken.token), ...parsed.restTokens].join(" ");
    const grouped = groupRcnAddresses({parsed, base, scoped: input.scoped, context: input.context});
    const addressOptions = {isHiddenCountExact: !base.capped};

    let addressSection: IPricesSearchSection | null;
    if (parsed.numberToken === undefined) {
        const autoExpandedKey = getAutoExpandedStreetKey(parsed, grouped.streets);
        const expandedKey = input.ui.expandedStreetKey === undefined ? autoExpandedKey : input.ui.expandedStreetKey;
        const streets = grouped.streets.map((street) => buildStreetItem(street, expandedKey, highlightQuery, input));
        addressSection = buildSection("streets", streets, addressOptions, input);
    } else {
        const addresses = grouped.addresses.map((row) => buildAddressItem(row, highlightQuery));
        addressSection = buildSection("addresses", addresses, addressOptions, input);
    }

    return [buildCitiesSection(parsed, highlightQuery, input), /* buildDistrictsSection(parsed, highlightQuery, input),*/ addressSection].filter(
        (section): section is IPricesSearchSection => section !== null
    );
};

// ---------------------------------------------------------------------------------------------------------------
// Focus content: nearby, recent, popular
// ---------------------------------------------------------------------------------------------------------------

const buildRecentItem = (entry: IPricesRecentSearch): PricesSearchSectionItem | null => {
    const common = {
        id: `recent:${entry.id}`,
        isNavigation: true,
        title: entry.title,
        highlightRanges: [],
        subtitle: entry.subtitle,
        href: entry.href,
        recentId: entry.id,
        citySlug: entry.citySlug
    };
    const cityName = PRICES_CITY_BY_SLUG.get(entry.citySlug)?.name;

    switch (entry.kind) {
        case "city":
            return {...common, kind: "city", searchLabel: entry.title, regionId: entry.regionId ?? getPricesCityRegionId(entry.citySlug)};
        case "district":
            return entry.regionId === undefined
                ? null
                : {...common, kind: "district", searchLabel: cityName ? `${entry.title}, ${cityName}` : entry.title, regionId: entry.regionId};
        case "address":
            return entry.rcn ? {...common, kind: "address", searchLabel: entry.title, rcn: entry.rcn} : null;
        default:
            return null;
    }
};

const buildRecentSection = (input: IBuildPricesSearchModelInput): IPricesSearchSection | null => {
    const items = input.recent
        .slice(0, PRICES_SEARCH_RECENT_SHOWN)
        .map(buildRecentItem)
        .filter((item): item is PricesSearchSectionItem => item !== null);
    if (items.length === 0) {
        return null;
    }

    const action: IPricesSearchClearRecentItem = {id: "recent:clearRecent", kind: "clearRecent", isNavigation: false, label: COPY.clearRecent};

    return {kind: "recent", title: COPY.groups.recent, action, items, hiddenCount: 0, isHiddenCountExact: true, isExpanded: false};
};

const getPopularCities = (): PricesCity[] => PRICES_SEARCH_POPULAR_CITY_SLUGS.flatMap((slug) => PRICES_CITY_BY_SLUG.get(slug) ?? []);

const buildPopularSection = (input: IBuildPricesSearchModelInput): IPricesSearchSection => {
    // A recent city is repeated here on purpose; the section prefix keeps the ids unique.
    const items = getPopularCities().map((city) => buildCityItem("popular", city, "", input));

    return {kind: "popular", title: COPY.groups.popular, items, hiddenCount: 0, isHiddenCountExact: true, isExpanded: false};
};

const buildNearbyItem = (input: IBuildPricesSearchModelInput): IPricesSearchNearbyItem => {
    const {nearby} = input;
    const subtitle =
        nearby.status === "blocked"
            ? COPY.nearby.blocked
            : nearby.status === "failed"
              ? COPY.nearby.failed
              : nearby.status === "outsideCoverage"
                ? COPY.nearby.outsideCoverage(nearby.placeName)
                : COPY.nearby.subtitle;

    return {
        id: "focus:nearby",
        kind: "nearby",
        // Leaves the page only once located; before that selecting it asks for the location.
        isNavigation: nearby.status === "resolved",
        title: COPY.nearby.title,
        highlightRanges: [],
        subtitle,
        searchLabel: nearby.status === "resolved" ? nearby.target.title : COPY.nearby.title,
        ...(nearby.status === "resolved" ? {href: nearby.target.href} : {}),
        state: nearby
    };
};

const buildChipItem = (city: PricesCity): IPricesSearchChipItem => ({
    id: `empty:chip:${city.slug}`,
    kind: "chip",
    isNavigation: true,
    label: city.name,
    citySlug: city.slug,
    regionId: getPricesCityRegionId(city.slug),
    href: getCityLink(city.slug)
});

// ---------------------------------------------------------------------------------------------------------------
// Navigation
// ---------------------------------------------------------------------------------------------------------------

/** Render order: section action, rows (a street, its numbers, their "show all"), the section "show more". */
const flattenSections = (sections: readonly IPricesSearchSection[]): PricesSearchNavigableItem[] =>
    sections.flatMap((section) => [
        ...(section.action ? [section.action] : []),
        ...section.items.flatMap((item): PricesSearchNavigableItem[] =>
            item.kind === "street" && item.numbers ? [item, ...item.numbers.items, ...(item.numbers.showMore ? [item.numbers.showMore] : [])] : [item]
        ),
        ...(section.showMore ? [section.showMore] : [])
    ]);

const RESULT_ROW_KINDS: ReadonlySet<PricesSearchItemKind> = new Set<PricesSearchItemKind>(["city", "district", "street", "address", "number"]);

const isUnavailableRow = (item: PricesSearchNavigableItem): boolean => (item.kind === "number" || item.kind === "address") && item.isUnavailable === true;

/**
 * The first result: an expanded street hands it to its first number, a collapsed one keeps it (Enter expands). A
 * number or an address without data passes it on to the next result row that has some - "Pokaż …" links are no
 * result Enter should confirm.
 */
const getAutoActiveId = (sections: readonly IPricesSearchSection[], navigation: readonly PricesSearchNavigableItem[]): string | null => {
    const first = sections[0]?.items[0];
    if (!first) {
        return null;
    }

    const firstId = first.kind === "street" && first.numbers?.items[0] ? first.numbers.items[0].id : first.id;
    const firstIndex = navigation.findIndex((item) => item.id === firstId);

    return navigation.slice(firstIndex).find((item) => RESULT_ROW_KINDS.has(item.kind) && !isUnavailableRow(item))?.id ?? null;
};

// ---------------------------------------------------------------------------------------------------------------
// Model
// ---------------------------------------------------------------------------------------------------------------

const buildFocusModel = (input: IBuildPricesSearchModelInput, isError: boolean): IPricesSearchModel => {
    const nearby = buildNearbyItem(input);
    const recent = buildRecentSection(input);
    const sections = [...(recent ? [recent] : []), buildPopularSection(input)];
    const retry: IPricesSearchRetryItem = {id: "banner:retry", kind: "retry", isNavigation: false, label: COPY.retry};
    const banner: IPricesSearchBanner = {kind: "error", retry};

    return {
        state: isError ? "error" : "focus",
        ...(isError ? {banner} : {}),
        nearby,
        sections,
        navigation: [...(isError ? [retry] : []), nearby, ...flattenSections(sections)],
        autoActiveId: null
    };
};

/**
 * The pure view model of the dropdown for one render - which state it is in, the sections with their
 * rows trimmed to the group limit, "Pokaż więcej" links, the banner, the empty state, the flattened navigation order
 * `useSearchCombobox` walks and the row Enter picks without a choice (`autoActiveId`).
 */
export const buildPricesSearchModel = (input: IBuildPricesSearchModelInput): IPricesSearchModel => {
    const {parsed, status} = input;

    if (parsed === null) {
        return buildFocusModel(input, false);
    }
    if (status === "error") {
        // The focus content with a retry banner on top; Enter retries.
        return buildFocusModel(input, true);
    }
    if (status === "debouncing" || status === "loading") {
        // From the keystroke until every answer for the phrase is in - the skeleton, never the previous
        // phrase's rows (the field shows the spinner meanwhile).
        return {state: "loading", sections: [], navigation: [], autoActiveId: null};
    }

    const isSettled = status === "success" || status === "empty";
    const base = input.base ?? (isSettled ? EMPTY_RESULT : null);
    // Not asked yet (the panel is closed): the focus content.
    if (base === null) {
        return buildFocusModel(input, false);
    }

    const sections = buildResultSections(parsed, base, input);
    if (sections.length > 0) {
        const diacriticsPhrase = getDiacriticsBannerPhrase(parsed, true);
        const banner: IPricesSearchBanner | null = diacriticsPhrase ? {kind: "diacritics", phrase: diacriticsPhrase} : null;
        const navigation = flattenSections(sections);

        return {
            state: "results",
            ...(banner ? {banner} : {}),
            sections,
            navigation,
            autoActiveId: getAutoActiveId(sections, navigation)
        };
    }
    if (!isSettled) {
        return buildFocusModel(input, false);
    }

    const cityWithoutData = input.regions?.citiesWithoutData[0];
    const chips = getPopularCities().map(buildChipItem);

    return {
        state: "empty",
        sections: [],
        empty: {
            variant: cityWithoutData ? "cityWithoutData" : "noResults",
            query: parsed.raw.trim(),
            ...(cityWithoutData ? {cityName: cityWithoutData.name} : {}),
            chips
        },
        navigation: chips,
        autoActiveId: null
    };
};

// ---------------------------------------------------------------------------------------------------------------
// Lazy prices of numbers and addresses
// ---------------------------------------------------------------------------------------------------------------

/** Rendered rows that get their price lazily, in render order: the numbers of the expanded street and the "Adresy" rows. */
export const getPricesSearchPricedAddresses = (model: IPricesSearchModel): IRcnAddressKey[] =>
    model.state !== "results"
        ? []
        : model.sections.flatMap((section) =>
              section.items.flatMap((item): IRcnAddressKey[] => {
                  if (item.kind === "street") {
                      return item.numbers ? item.numbers.items.map((number) => number.rcn) : [];
                  }

                  return item.kind === "address" && section.kind === "addresses" ? [item.rcn] : [];
              })
          );

const applyNumberPrice = <TItem extends IPricesSearchNumberItem | IPricesSearchAddressItem>(item: TItem, prices: PricesSearchNumberPrices): TItem => {
    const price = prices[getRcnAddressKey(item.rcn)];
    // Still loading or failed: the row stays as it is, and selectable.
    if (!price?.priceM2 && !price?.isUnavailable) {
        return item;
    }

    return {
        ...item,
        metrics: {...item.metrics, ...(price.priceM2 ? {priceM2: price.priceM2, ...(price.pricePeriod ? {pricePeriod: price.pricePeriod} : {})} : {})},
        ...(price.isUnavailable ? {isUnavailable: true} : {})
    };
};

/**
 * The prices `usePricesSearchNumberPrices` fetched for `getPricesSearchPricedAddresses(model)`, applied to the
 * model: the price goes into the row metrics, a row RCN publishes nothing for is flagged `isUnavailable` (the address
 * page would only redirect to `?adres_niedostepny=1`), and `autoActiveId` moves past such rows. A row still loading
 * stays selectable - most buildings turn out to have data.
 */
export const applyPricesSearchNumberPrices = (model: IPricesSearchModel, prices: PricesSearchNumberPrices): IPricesSearchModel => {
    if (model.state !== "results") {
        return model;
    }

    const sections = model.sections.map(
        (section): IPricesSearchSection => ({
            ...section,
            items: section.items.map((item): PricesSearchSectionItem => {
                if (item.kind === "street" && item.numbers) {
                    return {...item, numbers: {...item.numbers, items: item.numbers.items.map((number) => applyNumberPrice(number, prices))}};
                }

                return item.kind === "address" && section.kind === "addresses" ? applyNumberPrice(item, prices) : item;
            })
        })
    );
    const navigation = flattenSections(sections);

    return {...model, sections, navigation, autoActiveId: getAutoActiveId(sections, navigation)};
};
import {getHighlightRanges} from "@pg-design/search-combobox-module";
import {PRICES_CITIES} from "@pg-rp/navigation";

import {getPricesCityRegionId} from "../../constants/prices_cities_regions";
import {
    PRICES_SEARCH_COPY,
    PRICES_SEARCH_GROUP_LIMIT,
    PRICES_SEARCH_MIN_QUERY_LENGTH,
    PRICES_SEARCH_NUMBERS_PREVIEW,
    PRICES_SEARCH_POPULAR_CITY_SLUGS,
    PRICES_SEARCH_RECENT_SHOWN
} from "../../constants/prices_search";
import type {
    IBuildPricesSearchModelInput,
    IParsedPricesSearchQuery,
    IPricesAddressSearchResult,
    IPricesRecentSearch,
    IPricesRegionSuggestion,
    IPricesRegionSuggestionsResult,
    IPricesSearchAddressItem,
    IPricesSearchBanner,
    IPricesSearchChipItem,
    IPricesSearchCityItem,
    IPricesSearchClearRecentItem,
    IPricesSearchContext,
    IPricesSearchDistrictItem,
    IPricesSearchModel,
    IPricesSearchNearbyItem,
    IPricesSearchNumberItem,
    IPricesSearchNumberList,
    IPricesSearchRetryItem,
    IPricesSearchSection,
    IPricesSearchShowMoreItem,
    IPricesSearchStreetGroup,
    IPricesSearchStreetItem,
    PricesSearchItemKind,
    PricesSearchNavigableItem,
    PricesSearchNumberPrices,
    PricesSearchSectionItem,
    PricesSearchSectionKind
} from "../../types/IPricesSearchModel";
import type {IRcnAddressKey, IRcnAddressSuggestion} from "../../types/IRcnAddress";
import {getAddressLink} from "../get_address_link";
import {getCityLink} from "../get_city_link";
import {formatRcnAddressLabel, formatRcnCityName, formatRcnHouseNumber, formatRcnStreetName} from "./format_rcn_display_name";
import {getDistrictLink} from "./get_district_link";
import {getVoivodeshipByCitySlug} from "./get_voivodeship_by_city_slug";
import {getAutoExpandedStreetKey, getRcnAddressKey, groupRcnAddresses} from "./group_rcn_addresses";
import {findPricesCityByName, getDiacriticsBannerPhrase} from "./resolve_query_diacritics";
import {PRICES_SEARCH_MATCH_SCORE, scoreBestToken} from "./score_match";

type PricesCity = (typeof PRICES_CITIES)[number];
type ResultSectionKind = Extract<PricesSearchSectionKind, "cities" | "districts" | "streets" | "addresses">;
export type PricesCityDistrict = IPricesRegionSuggestion & {citySlug: string; cityName: string};

const COPY = PRICES_SEARCH_COPY;
const EMPTY_RESULT: IPricesAddressSearchResult = {rows: [], capped: false};
const PRICES_CITY_BY_SLUG: ReadonlyMap<string, PricesCity> = new Map(PRICES_CITIES.map((city) => [city.slug, city]));

const toSubtitle = (type: string, detail: string | null): string => (detail ? `${type}${COPY.subtitleSeparator}${detail}` : type);

const toHighlightRanges = (title: string, query: string) => (query ? getHighlightRanges(title, query) : []);

// ---------------------------------------------------------------------------------------------------------------
// Rows
// ---------------------------------------------------------------------------------------------------------------

const buildCityItem = (
    section: PricesSearchSectionKind,
    city: PricesCity,
    highlightQuery: string,
    input: IBuildPricesSearchModelInput
): IPricesSearchCityItem => ({
    id: `${section}:city:${city.slug}`,
    kind: "city",
    isNavigation: true,
    title: city.name,
    highlightRanges: toHighlightRanges(city.name, highlightQuery),
    subtitle: toSubtitle(COPY.subtitles.city, getVoivodeshipByCitySlug(city.slug)),
    metrics: input.metrics.byCitySlug[city.slug],
    href: getCityLink(city.slug),
    searchLabel: city.name,
    citySlug: city.slug,
    regionId: getPricesCityRegionId(city.slug)
});

const buildNumberItem = (row: IRcnAddressSuggestion, citySlug: string): IPricesSearchNumberItem => ({
    id: `streets:number:${getRcnAddressKey(row)}`,
    kind: "number",
    isNavigation: true,
    title: formatRcnHouseNumber(row.number),
    // Building numbers are never bold.
    highlightRanges: [],
    metrics: {transactionsCount: row.transactions_count},
    href: getAddressLink(row),
    searchLabel: formatRcnAddressLabel(row),
    citySlug,
    rcn: {city: row.city, street: row.street, number: row.number}
});

const buildNumberList = (street: IPricesSearchStreetGroup, cityName: string, input: IBuildPricesSearchModelInput): IPricesSearchNumberList => {
    const isShowingAll = input.ui.expandedNumberStreetKeys.includes(street.key);
    const isLoading = input.ui.loadingShowMoreKeys.includes(street.key);
    const allItems = street.rows.map((row) => buildNumberItem(row, street.citySlug));
    const items = isShowingAll ? allItems : allItems.slice(0, PRICES_SEARCH_NUMBERS_PREVIEW);
    const hiddenCount = allItems.length - items.length;
    // An incomplete street may have more numbers than we hold - the link then loads them, without a count.
    const hasMore = !isShowingAll && (hiddenCount > 0 || !street.isComplete);

    return {
        items,
        totalCount: allItems.length,
        isTotalExact: street.isComplete,
        isLoading,
        label: `${COPY.numbersGroupLabel}: ${street.title}, ${cityName}`,
        ...(hasMore
            ? {
                  showMore: {
                      id: `streets:showMore:${street.key}`,
                      kind: "showMore",
                      isNavigation: false,
                      target: "numbers",
                      label: street.isComplete ? `${COPY.showAllNumbers} (${allItems.length})` : COPY.showAllNumbers,
                      hiddenCount: street.isComplete ? hiddenCount : null,
                      streetKey: street.key,
                      isLoading
                  }
              }
            : {})
    };
};

const buildStreetItem = (
    street: IPricesSearchStreetGroup,
    expandedKey: string | null,
    highlightQuery: string,
    input: IBuildPricesSearchModelInput
): IPricesSearchStreetItem => {
    const cityName = formatRcnCityName(street.rawCity);
    const isExpanded = street.key === expandedKey;

    return {
        id: `streets:street:${street.key}`,
        kind: "street",
        // Selecting a street toggles its numbers - there is no street page.
        isNavigation: false,
        title: street.title,
        highlightRanges: toHighlightRanges(street.title, highlightQuery),
        subtitle: toSubtitle(COPY.subtitles.street, cityName),
        // No price per street in RCN; the sum is a lower bound while the street is incomplete.
        metrics: {transactionsCount: street.transactionsSum, transactionsIsLowerBound: !street.isComplete},
        searchLabel: `${street.title}, ${cityName}`,
        citySlug: street.citySlug,
        rcn: {city: street.rawCity, street: street.rawStreet},
        streetKey: street.key,
        isExpanded,
        ...(isExpanded ? {numbers: buildNumberList(street, cityName, input)} : {})
    };
};

const buildAddressItem = (row: IRcnAddressSuggestion, highlightQuery: string): IPricesSearchAddressItem => {
    const city = findPricesCityByName(row.city);
    const cityName = city?.name ?? formatRcnCityName(row.city);
    const title = `${formatRcnStreetName(row.street)} ${formatRcnHouseNumber(row.number)}`;

    return {
        id: `addresses:address:${getRcnAddressKey(row)}`,
        kind: "address",
        isNavigation: true,
        title,
        highlightRanges: toHighlightRanges(title, highlightQuery),
        subtitle: toSubtitle(COPY.subtitles.address, cityName),
        metrics: {transactionsCount: row.transactions_count},
        href: getAddressLink(row),
        searchLabel: formatRcnAddressLabel(row),
        // Grouped rows all belong to one of the 14 cities.
        citySlug: city?.slug ?? "",
        rcn: {city: row.city, street: row.street, number: row.number}
    };
};

// ---------------------------------------------------------------------------------------------------------------
// Sections
// ---------------------------------------------------------------------------------------------------------------

interface ISectionOptions {
    /** False when the source response was capped: the section may hold more than we know. */
    isHiddenCountExact: boolean;
}

const buildSection = (
    kind: ResultSectionKind,
    allItems: PricesSearchSectionItem[],
    {isHiddenCountExact}: ISectionOptions,
    input: IBuildPricesSearchModelInput
): IPricesSearchSection | null => {
    if (allItems.length === 0) {
        return null;
    }

    const isExpanded = input.ui.expandedSections.includes(kind);
    const items = isExpanded ? allItems : allItems.slice(0, PRICES_SEARCH_GROUP_LIMIT);
    const hiddenCount = allItems.length - items.length;
    const hasMore = !isExpanded && (hiddenCount > 0 || !isHiddenCountExact);
    const showMore: IPricesSearchShowMoreItem = {
        id: `${kind}:showMore`,
        kind: "showMore",
        isNavigation: false,
        target: kind,
        // "więcej" counts the rows left; with a capped response the number is unknown, so it is left out.
        label: isHiddenCountExact ? `${COPY.showMore[kind]} (${hiddenCount})` : COPY.showMore[kind],
        hiddenCount: isHiddenCountExact ? hiddenCount : null,
        isLoading: input.ui.loadingShowMoreKeys.includes(kind)
    };

    return {
        kind,
        title: COPY.groups[kind],
        items,
        hiddenCount,
        isHiddenCountExact,
        isExpanded,
        ...(hasMore ? {showMore} : {})
    };
};

// A city named in the query only scopes the streets once other words follow: "warszawa puławska" lists the street,
// not Warszawa, so Enter picks the street. Words shorter than the minimum query ("warszawa k") match no city.
const getCityMatchTokens = (parsed: IParsedPricesSearchQuery): string[] => [
    ...parsed.restTokens.filter((token) => token.length >= PRICES_SEARCH_MIN_QUERY_LENGTH),
    ...(parsed.restTokens.length === 0 && parsed.numberToken === undefined ? parsed.cityTokens.map((cityToken) => cityToken.token) : [])
];

const buildCitiesSection = (parsed: IParsedPricesSearchQuery, highlightQuery: string, input: IBuildPricesSearchModelInput): IPricesSearchSection | null => {
    const tokens = getCityMatchTokens(parsed);
    const transactionsCount = (city: PricesCity) => input.metrics.byCitySlug[city.slug]?.transactionsCount ?? 0;
    const cities = PRICES_CITIES.map((city) => ({city, score: scoreBestToken(city.name, tokens)}))
        .filter(({score}) => score >= PRICES_SEARCH_MATCH_SCORE.wordPrefix)
        .sort((a, b) => b.score - a.score || transactionsCount(b.city) - transactionsCount(a.city))
        .map(({city}) => buildCityItem("cities", city, highlightQuery, input));

    return buildSection("cities", cities, {isHiddenCountExact: true}, input);
};

/**
 * Districts the model lists, in order: those with a prices page, the current city's first (API order kept within both
 * groups). The region API matches any word of `full_name`, so "warszawa puławska" brings every Warsaw district - once
 * other words than a city are typed, a district has to match them by name, and belong to a typed city if there is one.
 */
export const getPricesSearchDistricts = (
    parsed: IParsedPricesSearchQuery,
    regions: IPricesRegionSuggestionsResult | null,
    context: IPricesSearchContext
): PricesCityDistrict[] => {
    const queryCitySlugs = new Set(parsed.cityTokens.map((cityToken) => cityToken.citySlug));
    const matchesQuery = (district: PricesCityDistrict): boolean =>
        parsed.restTokens.length === 0 ||
        (scoreBestToken(district.name, parsed.restTokens) >= PRICES_SEARCH_MATCH_SCORE.wordPrefix &&
            (queryCitySlugs.size === 0 || queryCitySlugs.has(district.citySlug)));

    return (
        (regions?.districts ?? [])
            .filter((district): district is PricesCityDistrict => district.hasPricesPage && district.citySlug !== null && district.cityName !== null)
            .filter(matchesQuery)
            // Stable: the API order stays within both groups.
            .sort((a, b) => Number(b.citySlug === context.citySlug) - Number(a.citySlug === context.citySlug))
    );
};

// Districts suggestions hidden for now, may return later.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const buildDistrictsSection = (parsed: IParsedPricesSearchQuery, highlightQuery: string, input: IBuildPricesSearchModelInput): IPricesSearchSection | null => {
    const districts = getPricesSearchDistricts(parsed, input.regions, input.context).map(
        (district): IPricesSearchDistrictItem => ({
            id: `districts:district:${district.regionId}`,
            kind: "district",
            isNavigation: true,
            title: district.name,
            highlightRanges: toHighlightRanges(district.name, highlightQuery),
            subtitle: toSubtitle(COPY.subtitles.district, district.cityName),
            metrics: input.metrics.byRegionId[district.regionId],
            href: getDistrictLink(district.citySlug, district.region),
            searchLabel: `${district.name}, ${district.cityName}`,
            citySlug: district.citySlug,
            regionId: district.regionId,
            region: district.region
        })
    );

    // A truncated first page may hide more districts than the ones counted.
    return buildSection("districts", districts, {isHiddenCountExact: !input.regions?.isTruncated}, input);
};

const buildResultSections = (
    parsed: IParsedPricesSearchQuery,
    base: IPricesAddressSearchResult,
    input: IBuildPricesSearchModelInput
): IPricesSearchSection[] => {
    // Bold what was typed for the title, the building number excluded (numbers are never bold).
    const highlightQuery = [...parsed.cityTokens.map((cityToken) => cityToken.token), ...parsed.restTokens].join(" ");
    const grouped = groupRcnAddresses({parsed, base, scoped: input.scoped, context: input.context});
    const addressOptions = {isHiddenCountExact: !base.capped};

    let addressSection: IPricesSearchSection | null;
    if (parsed.numberToken === undefined) {
        const autoExpandedKey = getAutoExpandedStreetKey(parsed, grouped.streets);
        const expandedKey = input.ui.expandedStreetKey === undefined ? autoExpandedKey : input.ui.expandedStreetKey;
        const streets = grouped.streets.map((street) => buildStreetItem(street, expandedKey, highlightQuery, input));
        addressSection = buildSection("streets", streets, addressOptions, input);
    } else {
        const addresses = grouped.addresses.map((row) => buildAddressItem(row, highlightQuery));
        addressSection = buildSection("addresses", addresses, addressOptions, input);
    }

    return [buildCitiesSection(parsed, highlightQuery, input), /* buildDistrictsSection(parsed, highlightQuery, input),*/ addressSection].filter(
        (section): section is IPricesSearchSection => section !== null
    );
};

// ---------------------------------------------------------------------------------------------------------------
// Focus content: nearby, recent, popular
// ---------------------------------------------------------------------------------------------------------------

const buildRecentItem = (entry: IPricesRecentSearch): PricesSearchSectionItem | null => {
    const common = {
        id: `recent:${entry.id}`,
        isNavigation: true,
        title: entry.title,
        highlightRanges: [],
        subtitle: entry.subtitle,
        href: entry.href,
        recentId: entry.id,
        citySlug: entry.citySlug
    };
    const cityName = PRICES_CITY_BY_SLUG.get(entry.citySlug)?.name;

    switch (entry.kind) {
        case "city":
            return {...common, kind: "city", searchLabel: entry.title, regionId: entry.regionId ?? getPricesCityRegionId(entry.citySlug)};
        case "district":
            return entry.regionId === undefined
                ? null
                : {...common, kind: "district", searchLabel: cityName ? `${entry.title}, ${cityName}` : entry.title, regionId: entry.regionId};
        case "address":
            return entry.rcn ? {...common, kind: "address", searchLabel: entry.title, rcn: entry.rcn} : null;
        default:
            return null;
    }
};

const buildRecentSection = (input: IBuildPricesSearchModelInput): IPricesSearchSection | null => {
    const items = input.recent
        .slice(0, PRICES_SEARCH_RECENT_SHOWN)
        .map(buildRecentItem)
        .filter((item): item is PricesSearchSectionItem => item !== null);
    if (items.length === 0) {
        return null;
    }

    const action: IPricesSearchClearRecentItem = {id: "recent:clearRecent", kind: "clearRecent", isNavigation: false, label: COPY.clearRecent};

    return {kind: "recent", title: COPY.groups.recent, action, items, hiddenCount: 0, isHiddenCountExact: true, isExpanded: false};
};

const getPopularCities = (): PricesCity[] => PRICES_SEARCH_POPULAR_CITY_SLUGS.flatMap((slug) => PRICES_CITY_BY_SLUG.get(slug) ?? []);

const buildPopularSection = (input: IBuildPricesSearchModelInput): IPricesSearchSection => {
    // A recent city is repeated here on purpose; the section prefix keeps the ids unique.
    const items = getPopularCities().map((city) => buildCityItem("popular", city, "", input));

    return {kind: "popular", title: COPY.groups.popular, items, hiddenCount: 0, isHiddenCountExact: true, isExpanded: false};
};

const buildNearbyItem = (input: IBuildPricesSearchModelInput): IPricesSearchNearbyItem => {
    const {nearby} = input;
    const subtitle =
        nearby.status === "blocked"
            ? COPY.nearby.blocked
            : nearby.status === "failed"
              ? COPY.nearby.failed
              : nearby.status === "outsideCoverage"
                ? COPY.nearby.outsideCoverage(nearby.placeName)
                : COPY.nearby.subtitle;

    return {
        id: "focus:nearby",
        kind: "nearby",
        // Leaves the page only once located; before that selecting it asks for the location.
        isNavigation: nearby.status === "resolved",
        title: COPY.nearby.title,
        highlightRanges: [],
        subtitle,
        searchLabel: nearby.status === "resolved" ? nearby.target.title : COPY.nearby.title,
        ...(nearby.status === "resolved" ? {href: nearby.target.href} : {}),
        state: nearby
    };
};

const buildChipItem = (city: PricesCity): IPricesSearchChipItem => ({
    id: `empty:chip:${city.slug}`,
    kind: "chip",
    isNavigation: true,
    label: city.name,
    citySlug: city.slug,
    regionId: getPricesCityRegionId(city.slug),
    href: getCityLink(city.slug)
});

// ---------------------------------------------------------------------------------------------------------------
// Navigation
// ---------------------------------------------------------------------------------------------------------------

/** Render order: section action, rows (a street, its numbers, their "show all"), the section "show more". */
const flattenSections = (sections: readonly IPricesSearchSection[]): PricesSearchNavigableItem[] =>
    sections.flatMap((section) => [
        ...(section.action ? [section.action] : []),
        ...section.items.flatMap((item): PricesSearchNavigableItem[] =>
            item.kind === "street" && item.numbers ? [item, ...item.numbers.items, ...(item.numbers.showMore ? [item.numbers.showMore] : [])] : [item]
        ),
        ...(section.showMore ? [section.showMore] : [])
    ]);

const RESULT_ROW_KINDS: ReadonlySet<PricesSearchItemKind> = new Set<PricesSearchItemKind>(["city", "district", "street", "address", "number"]);

const isUnavailableRow = (item: PricesSearchNavigableItem): boolean => (item.kind === "number" || item.kind === "address") && item.isUnavailable === true;

/**
 * The first result: an expanded street hands it to its first number, a collapsed one keeps it (Enter expands). A
 * number or an address without data passes it on to the next result row that has some - "Pokaż …" links are no
 * result Enter should confirm.
 */
const getAutoActiveId = (sections: readonly IPricesSearchSection[], navigation: readonly PricesSearchNavigableItem[]): string | null => {
    const first = sections[0]?.items[0];
    if (!first) {
        return null;
    }

    const firstId = first.kind === "street" && first.numbers?.items[0] ? first.numbers.items[0].id : first.id;
    const firstIndex = navigation.findIndex((item) => item.id === firstId);

    return navigation.slice(firstIndex).find((item) => RESULT_ROW_KINDS.has(item.kind) && !isUnavailableRow(item))?.id ?? null;
};

// ---------------------------------------------------------------------------------------------------------------
// Model
// ---------------------------------------------------------------------------------------------------------------

const buildFocusModel = (input: IBuildPricesSearchModelInput, isError: boolean): IPricesSearchModel => {
    const nearby = buildNearbyItem(input);
    const recent = buildRecentSection(input);
    const sections = [...(recent ? [recent] : []), buildPopularSection(input)];
    const retry: IPricesSearchRetryItem = {id: "banner:retry", kind: "retry", isNavigation: false, label: COPY.retry};
    const banner: IPricesSearchBanner = {kind: "error", retry};

    return {
        state: isError ? "error" : "focus",
        ...(isError ? {banner} : {}),
        nearby,
        sections,
        navigation: [...(isError ? [retry] : []), nearby, ...flattenSections(sections)],
        autoActiveId: null
    };
};

/**
 * The pure view model of the dropdown for one render - which state it is in, the sections with their
 * rows trimmed to the group limit, "Pokaż więcej" links, the banner, the empty state, the flattened navigation order
 * `useSearchCombobox` walks and the row Enter picks without a choice (`autoActiveId`).
 */
export const buildPricesSearchModel = (input: IBuildPricesSearchModelInput): IPricesSearchModel => {
    const {parsed, status} = input;

    if (parsed === null) {
        return buildFocusModel(input, false);
    }
    if (status === "error") {
        // The focus content with a retry banner on top; Enter retries.
        return buildFocusModel(input, true);
    }
    if (status === "debouncing" || status === "loading") {
        // From the keystroke until every answer for the phrase is in - the skeleton, never the previous
        // phrase's rows (the field shows the spinner meanwhile).
        return {state: "loading", sections: [], navigation: [], autoActiveId: null};
    }

    const isSettled = status === "success" || status === "empty";
    const base = input.base ?? (isSettled ? EMPTY_RESULT : null);
    // Not asked yet (the panel is closed): the focus content.
    if (base === null) {
        return buildFocusModel(input, false);
    }

    const sections = buildResultSections(parsed, base, input);
    if (sections.length > 0) {
        const diacriticsPhrase = getDiacriticsBannerPhrase(parsed, true);
        const banner: IPricesSearchBanner | null = diacriticsPhrase ? {kind: "diacritics", phrase: diacriticsPhrase} : null;
        const navigation = flattenSections(sections);

        return {
            state: "results",
            ...(banner ? {banner} : {}),
            sections,
            navigation,
            autoActiveId: getAutoActiveId(sections, navigation)
        };
    }
    if (!isSettled) {
        return buildFocusModel(input, false);
    }

    const cityWithoutData = input.regions?.citiesWithoutData[0];
    const chips = getPopularCities().map(buildChipItem);

    return {
        state: "empty",
        sections: [],
        empty: {
            variant: cityWithoutData ? "cityWithoutData" : "noResults",
            query: parsed.raw.trim(),
            ...(cityWithoutData ? {cityName: cityWithoutData.name} : {}),
            chips
        },
        navigation: chips,
        autoActiveId: null
    };
};

// ---------------------------------------------------------------------------------------------------------------
// Lazy prices of numbers and addresses
// ---------------------------------------------------------------------------------------------------------------

/** Rendered rows that get their price lazily, in render order: the numbers of the expanded street and the "Adresy" rows. */
export const getPricesSearchPricedAddresses = (model: IPricesSearchModel): IRcnAddressKey[] =>
    model.state !== "results"
        ? []
        : model.sections.flatMap((section) =>
              section.items.flatMap((item): IRcnAddressKey[] => {
                  if (item.kind === "street") {
                      return item.numbers ? item.numbers.items.map((number) => number.rcn) : [];
                  }

                  return item.kind === "address" && section.kind === "addresses" ? [item.rcn] : [];
              })
          );

const applyNumberPrice = <TItem extends IPricesSearchNumberItem | IPricesSearchAddressItem>(item: TItem, prices: PricesSearchNumberPrices): TItem => {
    const price = prices[getRcnAddressKey(item.rcn)];
    // Still loading or failed: the row stays as it is, and selectable.
    if (!price?.priceM2 && !price?.isUnavailable) {
        return item;
    }

    return {
        ...item,
        metrics: {...item.metrics, ...(price.priceM2 ? {priceM2: price.priceM2, ...(price.pricePeriod ? {pricePeriod: price.pricePeriod} : {})} : {})},
        ...(price.isUnavailable ? {isUnavailable: true} : {})
    };
};

/**
 * The prices `usePricesSearchNumberPrices` fetched for `getPricesSearchPricedAddresses(model)`, applied to the
 * model: the price goes into the row metrics, a row RCN publishes nothing for is flagged `isUnavailable` (the address
 * page would only redirect to `?adres_niedostepny=1`), and `autoActiveId` moves past such rows. A row still loading
 * stays selectable - most buildings turn out to have data.
 */
export const applyPricesSearchNumberPrices = (model: IPricesSearchModel, prices: PricesSearchNumberPrices): IPricesSearchModel => {
    if (model.state !== "results") {
        return model;
    }

    const sections = model.sections.map(
        (section): IPricesSearchSection => ({
            ...section,
            items: section.items.map((item): PricesSearchSectionItem => {
                if (item.kind === "street" && item.numbers) {
                    return {...item, numbers: {...item.numbers, items: item.numbers.items.map((number) => applyNumberPrice(number, prices))}};
                }

                return item.kind === "address" && section.kind === "addresses" ? applyNumberPrice(item, prices) : item;
            })
        })
    );
    const navigation = flattenSections(sections);

    return {...model, sections, navigation, autoActiveId: getAutoActiveId(sections, navigation)};
};
